<template>
  <section class="invoice-preview-wrapper">

    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="invoiceData === undefined"
    >
      <h4 class="alert-heading">
        Error fetching invoice data
      </h4>
      <div class="alert-body">
        No invoice found with this invoice id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'apps-invoice-list'}"
        >
          Invoice List
        </b-link>
        for other invoices.
      </div>
    </b-alert>

    <b-row
      v-if="invoiceData"
      class="invoice-preview"
    >

      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          ref="printNota"
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <b-card-body class="invoice-padding pb-0">

            <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">

              <!-- Header: Left Content -->
              <div>
                <div class="logo-wrapper">
                  <logo />
                </div>
                <p class="card-text mb-25">
                  {{ invoiceData.toko.name }}
                </p>
                <p class="card-text mb-25">
                  {{ invoiceData.toko.address }}
                </p>
                <p class="card-text mb-0">
                  Telp.{{ invoiceData.toko.phone }}
                </p>
              </div>

              <!-- Header: Right Content -->
              <div class="mt-md-0 mt-2">
                <h4 class="invoice-title">
                  Nota Pesanan
                  <span class="invoice-number">#{{ invoiceData.trx_no }}</span>
                </h4>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">
                    Tanggal:
                  </p>
                  <p class="invoice-date">
                    {{ invoiceData.trx_date }}
                  </p>
                </div>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">
                    Jatuh Tempo:
                  </p>
                  <p class="invoice-date">
                    {{ invoiceData.payment_due }}
                  </p>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Invoice Client & Payment Details -->
          <b-card-body
            v-if="invoiceData.customer"
            class="invoice-padding pt-0"
          >
            <b-row class="invoice-spacing">

              <!-- Col: Invoice To -->
              <b-col
                cols="12"
                xl="6"
                class="p-0"
              >
                <h6 class="mb-2">
                  Pesanan Dari:
                </h6>
                <h6 class="mb-25">
                  {{ invoiceData.customer.raw.name }}
                </h6>
                <b-card-text class="mb-0">
                  {{ invoiceData.customer.raw.email }}
                </b-card-text>
                <b-card-text class="mb-25">
                  {{ invoiceData.customer.raw.phone }}
                </b-card-text>
                <b-card-text class="mb-0">
                  {{ invoiceData.customer.raw.address_line1 }}, {{ invoiceData.customer.raw.village }} {{ invoiceData.customer.raw.subdistrict }} {{ invoiceData.customer.raw.city }}
                  {{ invoiceData.customer.raw.province }}
                </b-card-text>
              </b-col>

              <!-- Col: Payment Details -->
              <b-col
                xl="6"
                cols="12"
                class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end"
              >
                <div>
                  <h6 class="mb-2">
                    Detail Pembayaran:
                  </h6>
                  <table>
                    <tbody>
                      <tr class="mb-3">
                        <td class="pr-1">
                          Total Tagihan:
                        </td>
                        <td><span class="font-weight-bold">Rp{{ new Intl.NumberFormat('id-ID').format(invoiceData.grand_total) }}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-table-lite
            responsive
            :items="invoiceData.items"
            :fields="[
              { key: 'invoiceData', label: 'Produk' },
              { key: 'p', label: 'P' },
              { key: 'l', label: 'L' },
              { key: 'qty', label: 'Jumlah' },
              { key: 'price', label: 'Harga' },
              { key: 'discount', label: 'Diskon' },
              { key: 'subtotal', label: 'Total' }
            ]"
          >
            <template #cell(invoiceData)="data">
              <b-card-text class="font-weight-bold mb-25">
                {{ data.item.itemTitle.product }}
              </b-card-text>
              <b-card-text class="text-nowrap">
                {{ data.item.desc }}
              </b-card-text>
            </template>
          </b-table-lite>

          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0">
            <b-row>

              <!-- Col: Sales Persion -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-0 mt-3"
                order="2"
                order-md="1"
              >
                <b-card-text class="mb-0">
                  <span class="font-weight-bold">Kasir / PIC :</span>
                  <span class="ml-75">{{ invoiceData.pic }}</span>
                </b-card-text>
              </b-col>

              <!-- Col: Total -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-6 d-flex justify-content-end"
                order="1"
                order-md="2"
              >
                <div class="invoice-total-wrapper">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Total:
                    </p>
                    <p class="invoice-total-amount">
                      {{ new Intl.NumberFormat('id-ID').format(invoiceData.total) }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Diskon:
                    </p>
                    <p class="invoice-total-amount">
                      {{ new Intl.NumberFormat('id-ID').format(invoiceData.disc_exp) }}
                    </p>
                  </div>
                  <hr class="my-50">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Total Tagihan:
                    </p>
                    <p class="invoice-total-amount">
                      {{ new Intl.NumberFormat('id-ID').format(invoiceData.grand_total) }}
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Note -->
          <b-card-body class="invoice-padding pt-0">
            <span class="font-weight-bold">Catatan: </span>
            <span>{{ invoiceData.desc }}</span>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-danger"
            class="mb-75"
            block
            @click="printThermalInvoice"
          >
            <feather-icon
              icon="PrinterIcon"
              class="mr-50"
            />
            Print Struk
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-warning"
            class="mb-75"
            block
            @click="printInvoice"
          >
            <feather-icon
              icon="PrinterIcon"
              class="mr-50"
            />
            Print Nota
          </b-button>
        </b-card>
      </b-col>
    </b-row>
    <PrintNota
      v-if="dataReady"
      ref="nota"
      class="d-none"
      :data="invoiceData"
    />
    <PrintLabel
      v-if="dataReady"
      ref="notaLabel"
      class="d-none"
      :data="invoiceData"
    />
    <PrintSpk
      v-if="dataReady"
      ref="spk"
      class="d-none"
      :data="invoiceData"
    />
  </section>
</template>

<script>
import { ref, onUnmounted, getCurrentInstance } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import router from '@/router'
import store from '@/store'
import {
  BRow, BCol, BCard, BCardBody, BTableLite, BCardText, BButton, BAlert, BLink, VBToggle,
} from 'bootstrap-vue'
import Logo from '@core/layouts/components/Logo.vue'
import PrintNota from '@/views/feature/PrintNota.vue'
import Ripple from 'vue-ripple-directive'
import invoiceStoreModule from '../invoiceStoreModule'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    PrintNota,
    Logo,
  },
  data() {
    return {
      dataReady: false,
    }
  },
  setup() {
    const { proxy } = getCurrentInstance()

    const invoiceData = ref({
      customer: null, // mandatory
      id: null, // mandatory

      // ? Set single Item in form for adding data
      items: [],

      salesPerson: '',
      description: 'Penjualan ini dilakukan pada ',
      grand_total: 0,
      total: 0,
      disc_exp: 0,
      trx_date: new Date().toISOString().slice(0, 10),
      total_paid: 0,
      payment_due: null,
      cash_id: null,
      prod_id: 1,
      shipping_id: {
        value: 1,
        shipping: 'AMBIL DI TOKO',
      },
      paidType: '2', // mandatory
      so_addresses: {
        name: '',
        phone: '',
        email: '',
        address_line1: '',
        postal_code: '',
        province: '',
        city: '',
        subdistrict: '',
        village: '',
      },
      toko: {},
    })

    const INVOICE_APP_STORE_MODULE_NAME = 'app-sales'

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME)
    })

    const itemsOptions = ref([])
    const itemsLoad = new Promise((resolve, reject) => {
      store.dispatch('app-sales/fetchProducts')
        .then(response => {
          itemsOptions.value = response.data.map(product => {
            let basePrice = 0
            product.prices.forEach(price => {
              if (price.min_sales === 1) {
                basePrice = price.price
              }
            })
            return {
              value: product.id,
              product: product.name,
              price: basePrice,
              unit: product.unit?.name || '',
              qty: 1,
              note: '',
              // eslint-disable-next-line eqeqeq
              is_stock: (product.is_stock == 1),
              p: product.is_stock,
              l: product.is_stock,
              prices: product.prices,
              editable_price: (product.editable_price === 1),
            }
          })

          resolve(itemsOptions)
        })
    })

    itemsLoad.then(result => {
      store.dispatch('app-sales/fetchPreSales', router.currentRoute.params.id)
        .then(res => {
          const ps = res.data

          invoiceData.value.psPerson = ps.pic
          invoiceData.value.id = ps.id

          invoiceData.value.customer = {
            value: ps.customer.id,
            customer: ps.customer.name,
            raw: ps.customer,
          }

          invoiceData.value.trx_date = ps.ps_date
          invoiceData.value.trx_no = ps.ps_no
          invoiceData.value.description = ps.desc
          invoiceData.value.grand_total = ps.grand_total
          invoiceData.value.total = ps.total
          invoiceData.value.disc_exp = ps.disc_exp
          invoiceData.value.payment_due = ps.payment_due
          invoiceData.value.total_paid = 0

          invoiceData.value.items = ps.details.map(item => {
            const selectedProduct = result.value.find(itm => itm.value === item.product_id)

            if (!selectedProduct) {
              console.warn(`Produk dengan ID ${item.product_id} tidak ditemukan di itemsOptions.`)
              return null
            }

            return {
              itemTitle: {
                value: selectedProduct.value,
                product: selectedProduct.product,
              },
              price: new Intl.NumberFormat('id-ID').format(item.price),
              discount: new Intl.NumberFormat('id-ID').format((item.price * item.l * item.p * item.qty) - item.subtotal),
              subtotal: new Intl.NumberFormat('id-ID').format(item.subtotal),
              unit: selectedProduct.unit,
              qty: item.qty,
              note: item.desc,
              p: item.p,
              l: item.l,
            }
          }).filter(Boolean) // hilangkan yang null

          store.dispatch('app-sales/fetchCompany')
            .then(comp => {
              invoiceData.value.toko = comp.data.data
              proxy.dataReady = true
            })
            .catch(error => {
              console.error('Gagal ambil data produk:', error)
            })
        })
        .catch(error => {
          console.error('Gagal ambil data sales:', error)
        })
    })

    const printInvoice = () => {
      const box = document.querySelector('.breadcrumbs-top') // atau '.box-class'
      box.style.display = 'none' // Sembunyikan elemen sebelum mencetak
      window.print()
      box.style.display = 'block' // Sembunyikan elemen sebelum mencetak
    }

    const printThermalInvoice = () => {
      proxy.$refs.nota.print()
    }

    const printThermalLabel = () => {
      proxy.$refs.notaLabel.print()
    }

    const printThermalSpk = () => {
      proxy.$refs.spk.print()
    }

    return {
      invoiceData,
      printInvoice,
      printThermalInvoice,
      printThermalLabel,
      printThermalSpk,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@media print {

  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
              > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
